// BewerbungsFragebogen.jsx
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import './BewerbungsFragebogen.scss';

/* -------------------------
   Fragen-Definitionen
------------------------- */
const entryQuestion = {
  id: "role",
  question: "Für welche Stelle interessierst Du dich?",
  options: [
    { label: "Objektleiter/in (m/w/d)", icon: false },
    { label: "Reinigungskraft (m/w/d)", icon: false },
    { label: "Ausbildung zum Gebäudereiniger (m/w/d)", icon: false },
    { label: "Ausbildung Kaufmann/frau für Büromanagement (m/w/d)", icon: false },
  ],
};

const einsatzgebietQuestion = {
  id: "einsatzgebiete",
  question: "Wo möchtest Du bevorzugt arbeiten?",
  helperText: "Mehrfachauswahl möglich",
  multiSelect: true,
  flexibleOption: "Ich bin beim Einsatzgebiet flexibel",
  options: [
    { label: "Nürnberg", icon: false },
    { label: "Fürth", icon: false },
    { label: "Schwabach", icon: false },
    { label: "Roth", icon: false },
    { label: "Erlangen", icon: false },
    { label: "Röthenbach a. d. Pegnitz", icon: false },
    { label: "Ansbach", icon: false },
    { label: "Ich bin beim Einsatzgebiet flexibel", icon: false },
  ],
};

const questionsForAzubi = [
  {
    id: "abschluss",
    question: "Welchen Schulabschluss hast Du oder wirst Du bald erreichen?",
    options: [
      { label: "Qualifizierender Mittelschulabschluss", icon: false },
      { label: "Mittlere Reife", icon: false },
      { label: "Noch kein Abschluss", icon: false },
      { label: "Anderer Abschluss", icon: false },
    ],
  },
  {
    id: "beginn",
    question: "Wann könntest Du mit der Ausbildung starten?",
    options: [
      { label: "sofort", icon: false },
      { label: "innerhalb der nächsten 3 Monate", icon: false },
      { label: "zum Ausbildungsstart (September)", icon: false },
    ],
  },
];

const questionsForGeselle = [
  {
    id: "abschluss",
    question: "Hast Du eine abgeschlossene Berufsausbildung?",
    options: [
      { label: "Ja", icon: false },
      { label: "Nein", icon: false },
    ],
  },
  {
    id: "erfahrung",
    question: "Wie viele Jahre Berufserfahrung hast Du?",
    options: [
      { label: "Keine", icon: false },
      { label: "1–3 Jahre", icon: false },
      { label: "3–5 Jahre", icon: false },
      { label: "> 5 Jahre", icon: false },
    ],
  },
  {
    id: "beginn",
    question: "Wann wäre Dein frühestmöglicher Eintritt bei uns?",
    options: [
      { label: "sofort", icon: false },
      { label: "1-3 Monate", icon: false },
      { label: "3-5 Monate", icon: false },
      { label: "5-8 Monate", icon: false },
    ],
  },
];

const questionsForObjektleiter = [
  {
    id: "fuehrung",
    question: "Hast Du bereits Führungserfahrung?",
    options: [
      { label: "Ja", icon: false },
      { label: "Nein", icon: false },
    ],
  },
  {
    id: "teamgroesse",
    question: "Wie viele Mitarbeiter hast Du bereits betreut?",
    options: [
      { label: "Keine", icon: false },
      { label: "1–5", icon: false },
      { label: "5–10", icon: false },
      { label: "> 10", icon: false },
    ],
  },
  {
    id: "branche",
    question: "Hast Du Erfahrung in der Gebäudereinigung?",
    options: [
      { label: "Ja", icon: false },
      { label: "Nein", icon: false },
    ],
  },
  {
    id: "beginn",
    question: "Wann kannst Du starten?",
    options: [
      { label: "sofort", icon: false },
      { label: "1-3 Monate", icon: false },
      { label: "3-6 Monate", icon: false },
    ],
  },
];

const finalTextQuestion = {
  id: "zusatz",
  question: "Möchtest Du uns noch etwas über Dich erzählen? (optional)",
  isTextArea: true,
};

/* -------------------------
   Komponente
------------------------- */
const BewerbungsFragebogen = ({ region = "nuernberg" }) => {
  const [role, setRole] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [contact, setContact] = useState({ name: "", telefon: "", email: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [sendDataSuccess, setSendDataSuccess] = useState(false);

  // nodeRef für Transition
  const nodeRef = useRef(null);

  // Die neue Einsatzgebiet-Abfrage wird nur für Nürnberg verwendet.
  // München bleibt unverändert, wenn die Komponente mit region="muenchen" aufgerufen wird.
  const isNuernberg = region.toLowerCase() === "nuernberg";

  const getQuestions = () => {
    switch (role) {
      case "Objektleiter/in (m/w/d)":
        return [
          entryQuestion,
          ...(isNuernberg ? [einsatzgebietQuestion] : []),
          ...questionsForObjektleiter,
          finalTextQuestion,
        ];
      case "Reinigungskraft (m/w/d)":
        return [
          entryQuestion,
          ...(isNuernberg ? [einsatzgebietQuestion] : []),
          ...questionsForGeselle,
          finalTextQuestion,
        ];
      case "Ausbildung zum Gebäudereiniger (m/w/d)":
        return [entryQuestion, ...questionsForAzubi, finalTextQuestion];
      case "Ausbildung Kaufmann/frau für Büromanagement (m/w/d)":
        return [entryQuestion, ...questionsForAzubi, finalTextQuestion];
      default:
        return [entryQuestion];
    }
  };

  const questions = getQuestions();
  const currentQuestion = questions[step];
  const totalQuestions = questions.length;
  const showProgress = step !== 0 && !showContactForm;
  const currentQuestionNumber = step + 1;

  const handleAnswer = (value) => {
    if (currentQuestion.id === "role") {
      // Beim Wechsel der Stelle alte, stellenspezifische Antworten entfernen.
      setRole(value);
      setAnswers({ role: value });
      setStep(1);
      return;
    }

    const updatedAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(updatedAnswers);

    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      setShowContactForm(true);
    }
  };

  const handleMultiSelect = (value) => {
    const questionId = currentQuestion.id;
    const flexibleOption = currentQuestion.flexibleOption;
    const currentValues = Array.isArray(answers[questionId])
      ? answers[questionId]
      : [];

    let updatedValues;

    if (value === flexibleOption) {
      // "Flexibel" ist exklusiv: alle anderen Orte werden abgewählt.
      updatedValues = currentValues.includes(value) ? [] : [value];
    } else {
      // Sobald ein konkreter Ort gewählt wird, wird "flexibel" entfernt.
      const withoutFlexible = currentValues.filter(
        (item) => item !== flexibleOption
      );

      updatedValues = withoutFlexible.includes(value)
        ? withoutFlexible.filter((item) => item !== value)
        : [...withoutFlexible, value];
    }

    setAnswers({ ...answers, [questionId]: updatedValues });
  };

  const handleMultiSelectContinue = () => {
    const selectedValues = Array.isArray(answers[currentQuestion.id])
      ? answers[currentQuestion.id]
      : [];

    if (selectedValues.length === 0) {
      return;
    }

    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      setShowContactForm(true);
    }
  };

  const handleBack = () => {
    if (showContactForm) {
      setShowContactForm(false);
      setStep(questions.length - 1);
    } else if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleContactSubmit = () => {
    if (!contact.name || !contact.email || !contact.telefon) {
      alert("Bitte Name, E-Mail und Telefon ausfüllen.");
      return;
    }

    // Für das bestehende PHP-Backend die Mehrfachauswahl als gut lesbaren
    // String übergeben, damit nicht versehentlich nur "Array" ausgegeben wird.
    const dataToSend = {
      ...answers,
      einsatzgebiete: Array.isArray(answers.einsatzgebiete)
        ? answers.einsatzgebiete.join(", ")
        : answers.einsatzgebiete,
      ...contact,
    };

    setIsLoading(true);
    axios({
      method: 'post',
      url: '/backend/index.php',
      headers: { 'Content-Type': 'application/json' },
      data: dataToSend,
    })
      .then(() => {
        setSendDataSuccess(true);
        setShowContactForm(false);
      })
      .catch(() => {
        alert("Fehler beim Senden. Bitte versuchen Sie es später erneut.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="fragebogen-container">
      {sendDataSuccess ? (
        <div className="success-box">
          🎉 Vielen Dank! Deine Bewerbung wurde erfolgreich gesendet. <br />
          Wir melden uns zeitnah bei Dir.
        </div>
      ) : showContactForm ? (
        <>
          <h2 className="headline">Deine Kontaktdaten</h2>
          <input
            type="text"
            placeholder="Name*"
            value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })}
            className="input-field"
          />
          <input
            type="tel"
            placeholder="Telefon*"
            value={contact.telefon}
            onChange={(e) => setContact({ ...contact, telefon: e.target.value })}
            className="input-field"
          />
          <input
            type="email"
            placeholder="E-Mail*"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            className="input-field"
          />
          <p className="datenschutz-text">
            Wir verarbeiten Ihre Daten zum Zwecke der Bearbeitung Ihrer Anfrage. Bitte beachten Sie unsere{' '}
            <a href="/datenschutz" target="_blank" rel="noreferrer">
              Datenschutzerklärung
            </a>.
          </p>
          <button
            onClick={handleContactSubmit}
            disabled={isLoading}
            className="btn btn-primary"
          >
            {isLoading ? "Sende..." : "Absenden"}
          </button>
          <button onClick={handleBack} className="btn btn-secondary">
            Zurück
          </button>
        </>
      ) : (
        <>
          {showProgress && (
            <>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${(currentQuestionNumber / totalQuestions) * 100}%`,
                  }}
                />
              </div>
              <div className="progress-text">
                Frage {currentQuestionNumber} von {totalQuestions}
              </div>
            </>
          )}

          {/* Fragen mit Animation */}
          <div
            className="question-container"
            style={{ height: nodeRef.current?.offsetHeight || 'auto' }}
          >
            <TransitionGroup component={null}>
              {questions.length > 0 &&
                (() => {
                  const transitionNodeRef = React.createRef();
                  const selectedValues = currentQuestion.multiSelect
                    ? Array.isArray(answers[currentQuestion.id])
                      ? answers[currentQuestion.id]
                      : []
                    : [];

                  return (
                    <CSSTransition
                      key={currentQuestion.id}
                      timeout={300}
                      classNames="slide"
                      nodeRef={transitionNodeRef}
                      unmountOnExit
                    >
                      <div ref={transitionNodeRef} className="question-wrapper">
                        <h2 className="headline">{currentQuestion.question}</h2>

                        {currentQuestion.helperText && (
                          <p className="question-helper-text">
                            {currentQuestion.helperText}
                          </p>
                        )}

                        <div className={`options-list${currentQuestion.multiSelect ? " options-list--multiselect" : ""}`}>
                          {currentQuestion.isTextArea ? (
                            <>
                              <textarea
                                rows={5}
                                className="textarea-field"
                                placeholder="Deine Nachricht hier..."
                                value={answers[currentQuestion.id] || ""}
                                onChange={(e) =>
                                  setAnswers({
                                    ...answers,
                                    [currentQuestion.id]: e.target.value,
                                  })
                                }
                              />
                              <button
                                onClick={() => setShowContactForm(true)}
                                className="btn btn-primary"
                              >
                                Weiter
                              </button>
                            </>
                          ) : currentQuestion.multiSelect ? (
                            <>
                              {currentQuestion.options.map((opt, idx) => {
                                const isSelected = selectedValues.includes(opt.label);

                                return (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handleMultiSelect(opt.label)}
                                    className={`btn btn-option btn-option-multiselect${
                                      isSelected ? ' is-selected' : ''
                                    }${
                                      opt.label === currentQuestion.flexibleOption
                                        ? ' is-flexible-option'
                                        : ''
                                    }`}
                                    aria-pressed={isSelected}
                                  >
                                    <span>{opt.label}</span>
                                    {isSelected && (
                                      <span className="option-check" aria-hidden="true">
                                        ✓
                                      </span>
                                    )}
                                  </button>
                                );
                              })}

                              <button
                                type="button"
                                onClick={handleMultiSelectContinue}
                                className="btn btn-primary btn-multiselect-continue"
                                disabled={selectedValues.length === 0}
                              >
                                Weiter
                              </button>
                            </>
                          ) : (
                            currentQuestion.options.map((opt, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleAnswer(opt.label)}
                                className="btn btn-option"
                              >
                                {opt.label}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </CSSTransition>
                  );
                })()}
            </TransitionGroup>
          </div>

          {step > 0 && (
            <button onClick={handleBack} className="btn btn-secondary">
              Zurück
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default BewerbungsFragebogen;